import React, {useState} from 'react';
import {Box, Button, TextField, Typography} from '@mui/material';
import {RecaptchaVerifier, signInWithPhoneNumber} from "firebase/auth";
import {auth} from "../firebase";
import Welcome from "../components/Welcome";
import {styled} from "@mui/material/styles";

// Styled headline typography for the large text
const HeadlineTypography = styled(Typography)(({theme}) => ({
    color: 'white',
    fontSize: '1.8rem', // Larger font for the headline
    fontWeight: 'bold',
    textAlign: 'center',
}));

// Styled supporting typography for the smaller text
const SupportingTypography = styled(Typography)(({theme}) => ({
    color: 'white',
    fontSize: '1rem', // Smaller font for supporting text
    textAlign: 'center',
    opacity: 0.8, // Make it slightly faded for a softer look
}));

function LoginPage({user, setUser, authUser}) {
    const [phoneNumber, setPhoneNumber] = useState('+972');
    const [verificationCode, setVerificationCode] = useState('');
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [error, setError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [loadingUser, setLoadingUser] = useState(false);

    const handleSignIn = async () => {
        setError('');
        if (!phoneNumber || phoneNumber.trim().length < 10) {
            setError('אנא הזן מספר טלפון תקין.');
            return;
        }

        setupRecaptcha();
        const appVerifier = window.recaptchaVerifier;

        try {
            const result = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
            setConfirmationResult(result);
        } catch (error) {
            console.error('Error during phone sign-in:', error);
            setError('שגיאה בעת הכניסה עם מספר טלפון.');
        }
    };

    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                size: 'invisible',
                callback: (response) => {
                    handleSignIn();
                },
            });
        }
    };

    const handleVerifyCode = async () => {
        setError('');
        if (verificationCode.trim() === '') {
            setError('אנא הזן קוד אימות.');
            return;
        }

        try {
            setIsVerifying(true);
            const result = await confirmationResult.confirm(verificationCode);
            setIsVerifying(false);
            setLoadingUser(true);
        } catch (error) {
            console.error('Error verifying code or updating displayName:', error);
            setError('קוד אימות שגוי.');
            setIsVerifying(false);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                bgcolor: 'background.default',
                color: 'text.primary',
                background: 'linear-gradient(180deg, rgba(21, 0, 53, 0.89) 0%, rgba(0, 116, 98, 0.84) 100%)',
                padding: '0 16px', // Add padding for responsiveness
            }}
        >
            <div id="recaptcha-container"></div>
            {authUser && !user && !loadingUser && (<Welcome setUser={setUser} authUser={authUser} isOpen={true}/>)}

            {/* Image in the Middle */}
            <Box sx={{mb: 4}}>
                <img src="illu-login.png" alt="Middle Image" style={{width: '100%'}}/>
            </Box>

            {/* Headline and Supporting Text */}
            <HeadlineTypography variant="h4" sx={{mb: 1}}>
                מטפל אישי בכף ידך
            </HeadlineTypography>
            <SupportingTypography variant="body1">
                כלי בינה מלאכותית לטיפול וייעוץ נפשי המבוסס על משנתו של אברהם ליפשיץ
            </SupportingTypography>
            <SupportingTypography variant="body2">
                היכנס וקבל שבועיים התנסות חינם
            </SupportingTypography>

            {/* Phone Number or Verification Code Input */}
            {!confirmationResult ? (
                <>
                    <TextField
                        variant="filled"
                        sx={{
                            input: {
                                color: 'white'
                            },
                            '& .MuiFilledInput-underline:before': {
                                borderBottomColor: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFilledInput-underline:after': {
                                borderBottomColor: 'white',
                            },
                            width: '100%', // Responsive width
                            maxWidth: 400,
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            mb: 2,
                            borderRadius: 2, // Slightly more rounded
                            marginTop: '20px',
                        }}
                        placeholder="מספר טלפון"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #00a3b1 30%, #0093e9 50%, #6e00b7 90%)',
                            width: '100%',
                            maxWidth: 400,
                            mb: 2,
                            color: 'white',
                            padding: '10px 30px',
                            borderRadius: 8,
                            boxShadow: '0px 3px 5px 2px rgba(110, 0, 183, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #6e00b7 30%, #0093e9 70%, #00a3b1 100%)',
                            },
                        }}
                        onClick={handleSignIn}
                    >
                        קבל קוד אימות
                    </Button>
                </>
            ) : (
                <>
                    <TextField
                        variant="filled"
                        sx={{
                            input: {
                                color: 'white'
                            },
                            '& .MuiFilledInput-underline:before': {
                                borderBottomColor: 'rgba(255, 255, 255, 0.7)',
                            },
                            '& .MuiFilledInput-underline:after': {
                                borderBottomColor: 'white',
                            },
                            width: '100%', // Responsive width
                            maxWidth: 400,
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            mb: 2,
                            borderRadius: 2, // Slightly more rounded
                            padding: '10px',
                        }}
                        placeholder="הזן את קוד האימות"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                    />
                    {error && <Typography color="error">{error}</Typography>}
                    <Button
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(45deg, #00a3b1 30%, #0093e9 50%, #6e00b7 90%)',
                            width: '100%',
                            maxWidth: 400,
                            mb: 2,
                            color: 'white',
                            padding: '10px 30px',
                            borderRadius: 8,
                            boxShadow: '0px 3px 5px 2px rgba(110, 0, 183, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #6e00b7 30%, #0093e9 70%, #00a3b1 100%)',
                            },
                        }}
                        onClick={handleVerifyCode}
                    >
                        {isVerifying ? 'מאמת...' : 'אמת קוד'}
                    </Button>
                </>
            )}

            <Typography variant="caption" sx={{mt: 2, color: 'grey.500', textAlign: 'center'}}>
                על ידי התחברות אתה מסכים ל
                <a href="#" sx={{color: 'grey.500', textDecoration: 'none'}}>מדיניות הפרטיות</a>
                ול
                <a href="#" sx={{color: 'grey.500', textDecoration: 'none'}}>תנאי השירות</a> שלנו.
            </Typography>
        </Box>
    );
}

export default LoginPage;
